import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Injectable
} from '@angular/core';

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from '@angular/material/dialog';

import {
    MatDivider
} from '@angular/material/divider';

import {
    MatIcon
} from '@angular/material/icon';

import {
    MatListOption,
    MatSelectionList,
    MatSelectionListChange
} from '@angular/material/list';

import {
    TranslateModule,
    TranslateService
} from '@ngx-translate/core';

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from '@tilby/tilby-ui-lib/components/tilby-dialog';

import {
    lastValueFrom
} from 'rxjs';

export type GenericListItem<T = any> = {
    name: string,
    value: T,
    isSelected?: boolean,
    preIcon?: string,
    noTranslate?: boolean
};

export interface GenericListData<T = any> {
    title: {
        label: string,
        params?: { [key: string]: string }
    },
    list: GenericListItem<T>[],
    showCheckbox?: boolean
}

@Component({
    selector: 'app-generic-list-dialog',
    templateUrl: './generic-list-dialog.component.html',
    styleUrls: ['./generic-list-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatIcon,
        MatDivider,
        MatSelectionList,
        MatListOption,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
    ]
})
export class GenericListDialogComponent {
    private translateService = inject(TranslateService);
    private dialogRef = inject(MatDialogRef);
    protected data: GenericListData<any> = inject(MAT_DIALOG_DATA);
    protected title = this.translateService.instant(this.data.title.label, this.data.title.params);

    selectionHandler(selectionListChange: MatSelectionListChange) {
        const lineSelected = selectionListChange.options[0].value;
        this.dialogRef.close(lineSelected.value);
    }

    cancel() {
        this.dialogRef.close(null);
    };
}

@Injectable({
    providedIn: 'root'
})
export class GenericListDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog<T = any>(options: NonNullableConfigData<GenericListData<T>>): Promise<T | undefined> {
        const config: NonNullableConfigData<GenericListData<T>> = {
            ...this.switchMobileDesktopDimensions({ width: '600px' }),
            disableClose: true,
            ...options
        };

        return lastValueFrom(this.dialogRef.open<GenericListDialogComponent, GenericListData<T>, T>(GenericListDialogComponent, config).afterClosed());
    }
}